import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthResult, AuthTokens, AuthenticatedUser, DuckColor, DuckSpecies } from '@duck/contracts';

/**
 * The last name + duck this device entered with — kept SEPARATELY from the live session so it
 * survives logout. A returning guest opening a NEW room's invite gets these prefilled, so re-entering
 * is "tap your duck, go" rather than re-typing everything. It is identity convenience, never a
 * credential (the actual session is the persisted refresh token below).
 */
interface LastIdentity {
  displayName: string;
  duckColor: DuckColor;
  /** The animal this device last entered as (ADR-0024), so a returning guest's pick is pre-selected. */
  species: DuckSpecies;
}

interface AuthState {
  user: AuthenticatedUser | null;
  tokens: AuthTokens | null;
  /** Prefill hint for the next entry form; updated on every successful auth. */
  lastIdentity: LastIdentity | null;
  isAuthenticated: () => boolean;
  setAuth: (result: AuthResult) => void;
  setTokens: (tokens: AuthTokens) => void;
  /**
   * Fold a self-service appearance change (ADR-0024) into the live session WITHOUT touching `tokens` —
   * appearance isn't a credential, so the access token is untouched, and swapping only `user` (same id +
   * workspaceId) never re-keys the room-connect effect, so an open room session isn't rebuilt. Also
   * refreshes `lastIdentity` so the next entry form pre-selects the new critter. No-op if signed out.
   */
  setAppearance: (appearance: { duckColor: DuckColor; species: DuckSpecies }) => void;
  /** Incidental session loss (e.g. a refresh token rejected) — drop the session but KEEP lastIdentity. */
  clear: () => void;
  /** Explicit, user-initiated sign-out — a full clean slate, lastIdentity included (no prefill on return). */
  logout: () => void;
}

/**
 * Auth session state, persisted to localStorage so a refresh keeps the user logged in — and, for
 * frictionless guest entry (Task 4), so a returning visitor's device IS their identity: a valid /
 * refreshable token means they skip the form entirely (the router sends them straight to /office).
 * Tokens here are the source of truth for the API client's Authorization header.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      lastIdentity: null,
      isAuthenticated: () => get().tokens !== null,
      setAuth: (result) =>
        set({
          user: result.user,
          tokens: result.tokens,
          lastIdentity: {
            displayName: result.user.displayName,
            duckColor: result.user.duckColor,
            species: result.user.species,
          },
        }),
      setTokens: (tokens) => set({ tokens }),
      setAppearance: (appearance) =>
        set((s) =>
          s.user
            ? {
                user: { ...s.user, ...appearance },
                lastIdentity: { displayName: s.user.displayName, ...appearance },
              }
            : {},
        ),
      // Incidental eviction (a background refresh got a 401) deliberately KEEPS lastIdentity so the
      // device can re-enter frictionlessly — it never meant to leave.
      clear: () => set({ user: null, tokens: null }),
      // A deliberate "Log out" tap is the opposite intent: the person wants OUT. Wipe everything,
      // including the name/duck prefill, so the next screen is unmistakably signed-out (otherwise a
      // prefilled identity reads as "I'm still logged in" — the very reason logout felt broken).
      logout: () => set({ user: null, tokens: null, lastIdentity: null }),
    }),
    { name: 'duck-duck-office-auth' },
  ),
);
